import React, { useEffect, useMemo } from 'react'
import { BrowserRouter as Router, Route, Redirect } from 'react-router-dom'
import { Container } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { setUserInfo } from './slices/userSlice'
import { saveServerCart } from './slices/cartSlice'
import useSyncCartOnLogin from './hooks/useSyncCartOnLogin'
import {
  clearHomeRestoreState,
  getAuthSignature,
  getHomeAuthSignature,
  isHardReloadNavigation,
  setHomeAuthSignature,
} from './utils/homeRestoreState'
import Header from './components/Header'
import Footer from './components/Footer'
import HomeScreen from './screens/HomeScreen'
import ProductScreen from './screens/ProductScreen'
import CartScreen from './screens/CartScreen'
import LoginScreen from './screens/LoginScreen'
import RegisterScreen from './screens/RegisterScreen'
import RegisterConfirmationScreen from './screens/RegisterConfirmationScreen'
import ProfileScreen from './screens/ProfileScreen'
import ShippingScreen from './screens/ShippingScreen'
import ShippingRateScreen from './screens/ShippingRateScreen'
import PlaceOrderScreen from './screens/PlaceOrderScreen'
import OrderScreen from './screens/OrderScreen'
import OrderCancelScreen from './screens/OrderCancelScreen'
import OrderRefundRequestScreen from './screens/OrderRefundRequestScreen'
import UserListScreen from './screens/UserListScreen'
import UserEditScreen from './screens/UserEditScreen'
import ProductListScreen from './screens/ProductListScreen'
import ProductEditScreen from './screens/ProductEditScreen'
import ProductQuickImportScreen from './screens/admin/ProductQuickImportScreen'
import AliExpressConnectedScreen from './screens/AliExpressConnectedScreen'
import OrderListScreen from './screens/OrderListScreen'
import AdminCategoryManagerScreen from './screens/AdminCategoryManagerScreen'
import AdminServiceClientsScreen from './screens/AdminServiceClientsScreen'
import ConfirmationScreen from './screens/ConfirmationScreen'
import ForgotPasswordScreen from './screens/ForgotPasswordScreen'
import ResetPasswordScreen from './screens/ResetPasswordScreen'
import ResendConfirmationScreen from './screens/ResendConfirmationScreen'
import ContactUs from './screens/ContactUsScreen'
import SupportMessageScreen from './screens/SupportMessageScreen'
import GuestGuard from './components/GuestGuard'
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen'
import TermsScreen from './screens/TermsScreen'
import LandingPageTest from './screens/LandingPageTest'

const App = () => {
  // central restore
  const dispatch = useDispatch?.()
  const userInfo = useSelector?.(s => s.user?.userInfo)
  const cartItems = useSelector?.(s => s.cart?.cartItems || [])

  const authSig = useMemo(() => getAuthSignature(userInfo), [userInfo])

  // Reset Home restore state on hard reload.
  useEffect(() => {
    if (isHardReloadNavigation()) {
      clearHomeRestoreState()
    }
    // Ensure the auth sig is set early.
    setHomeAuthSignature(authSig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset Home restore state whenever auth changes (login/logout/account switch).
  useEffect(() => {
    const prev = getHomeAuthSignature()
    if (prev && prev !== authSig) {
      clearHomeRestoreState()
    }
    setHomeAuthSignature(authSig)
  }, [authSig])
  useEffect(() => {
    const hasGuestCookie = typeof document !== 'undefined' && document.cookie.includes('guestToken=')
    if (!userInfo && hasGuestCookie) {
      axios.get('/api/users/session', { withCredentials: true })
        .then(r => { dispatch && dispatch(setUserInfo(r.data)); try { localStorage.setItem('userInfo', JSON.stringify(r.data)) } catch {} })
        .catch(() => {})
    }
  }, [userInfo, dispatch])

  // Fetch server cart after a non-guest user logs in (see hook for why this
  // isn't a plain inline effect keyed on the userInfo object).
  useSyncCartOnLogin(userInfo, dispatch)

  // Auto-save cart to server whenever it changes (debounced)
  useEffect(() => {
    if (!userInfo || userInfo.isGuest) return
    const id = setTimeout(() => {
      dispatch && dispatch(saveServerCart(cartItems))
    }, 1000)
    return () => clearTimeout(id)
  }, [cartItems, userInfo, dispatch])

  return (
    <Router>
      
      
        <Header /> 
        <GuestGuard />
        

        
       {/* <StackedExample className='d-inline sticky-top-side bg-primary' display="block" /> */}
      {/* <Top className=' sticky-top bg-primary' /> */}
      <main className='py-3'>
        <Container fluid>
          <Route path='/order/:id/cancel' component={OrderCancelScreen} />
          <Route path='/order/:id/request-refund' component={OrderRefundRequestScreen} />
          <Route path='/order/:id' component={OrderScreen} exact />
          <Route path='/shipping' component={ShippingScreen} />
          <Route path='/shippingrate' component={ShippingRateScreen} />
          <Route path='/payment' render={() => <Redirect to='/placeorder' />} />
          <Route path='/placeorder' component={PlaceOrderScreen} />
          <Route path='/login' component={LoginScreen} />
          <Route path='/register' component={RegisterScreen}/>
          <Route path='/registerconfirm/:token' component={RegisterConfirmationScreen}/>
          <Route path='/privacy' component={PrivacyPolicyScreen} />
          <Route path='/terms' component={TermsScreen} />
          <Route path='/user/confirm/:token' component={ConfirmationScreen}/>  
          <Route path='/contact' component={ContactUs}/>  
          <Route path='/contactSupport' component={SupportMessageScreen}/>  
          <Route path='/resendconfirmation' component={ResendConfirmationScreen} />
          <Route path='/forgotpw' component={ForgotPasswordScreen} />
          <Route path='/users/resetpw/:token' component={ResetPasswordScreen}/>
          <Route path='/profile' component={ProfileScreen} />
          <Route path='/product/:id' component={ProductScreen} />
          <Route path='/cart/:id?' component={CartScreen} />
          <Route path='/admin/userlist' component={UserListScreen} />
          <Route path='/admin/user/:id/edit' component={UserEditScreen} />
          <Route
            path='/admin/productlist'
            component={ProductListScreen}
            exact
          />
          <Route
            path='/admin/productlist/:pageNumber'
            component={ProductListScreen}
            exact
          />
          <Route path='/admin/product/quick-import' component={ProductQuickImportScreen} />
          <Route path='/aliexpress/connected' component={AliExpressConnectedScreen} exact />
          <Route path='/admin/product/:id/edit' component={ProductEditScreen} />
          <Route path='/admin/orderlist/:pageNumber?' component={OrderListScreen} />
          <Route path='/admin/categories' component={AdminCategoryManagerScreen} />
          <Route path='/admin/aliexpress-service-clients' component={AdminServiceClientsScreen} />
          <Route path='/landing-test' component={LandingPageTest} exact />
          <Route
            path='/search/:keyword'
            exact
            render={(props) => <HomeScreen {...props} key={`home:${authSig}`} />}
          />
          <Route
            path='/page/:pageNumber'
            exact
            render={(props) => <HomeScreen {...props} key={`home:${authSig}`} />}
          />
          <Route
            path='/search/:keyword/page/:pageNumber'
            exact
            render={(props) => <HomeScreen {...props} key={`home:${authSig}`} />}
          />
          <Route
            path='/'
            exact
            render={(props) => <HomeScreen {...props} key={`home:${authSig}`} />}
          />
        </Container>
      </main>
      <Footer />
    </Router>
  )
}

export default App
